import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Bot,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Copy,
  KeyRound,
  Link2,
  LockKeyhole,
  Radio,
  RefreshCw,
  Route,
  Send,
  Settings2,
  ShieldCheck,
  Terminal,
  UserRound,
  WalletCards,
  Zap,
} from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const STORAGE_KEY = "aeon_api_trading_setup_v1";
const QUEUE_KEY = "aeon_api_trading_drafts_v1";

const DEFAULT_SETUP = {
  telegramSource: "",
  executor: "unassigned",
  executorName: "",
  mode: "draft",
  maxRisk: "0.50",
  maxLeverage: "5",
  dailyStop: "3.00",
  approval: "always",
};

const DEFAULT_DRAFT = {
  symbol: "BTCUSDT",
  side: "LONG",
  orderType: "LIMIT",
  entry: "",
  stop: "",
  targets: "",
  risk: "0.50",
  leverage: "5",
};

const EXAMPLE_CALL = {
  id: "example-call",
  symbol: "BTCUSDT",
  side: "LONG",
  entry: "Call price",
  stop: "Required",
  targets: ["TP1", "TP2"],
  confidence: null,
  source: "@aeon_calls",
  status: "PREVIEW",
  timestamp: null,
  isExample: true,
  raw: "Example layout — a real Telegram call will replace this card.",
};

function readJson(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value && typeof value === "object" ? value : fallback;
  } catch {
    return fallback;
  }
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function cleanNumber(value) {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(String(value).replace(/[$,%\s,]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function displayLevel(value) {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "number") {
    return value.toLocaleString(undefined, { maximumFractionDigits: 8 });
  }
  return String(value).replace(/\s+/g, " ").trim();
}

function parseTaggedLevel(text, tags) {
  const tagGroup = tags.join("|");
  const match = text.match(
    new RegExp(`(?:${tagGroup})\\s*(?:PRICE)?\\s*[:=@-]?\\s*\\$?([\\d,.]+(?:\\s*[-–]\\s*\\$?[\\d,.]+)?)`, "i")
  );
  return match?.[1] || null;
}

function parseTargets(text) {
  const explicit = [...text.matchAll(/\bTP\d*\s*[:=@-]?\s*\$?([\d,.]+)/gi)].map((match) => match[1]);
  if (explicit.length) return explicit.slice(0, 4);
  const single = parseTaggedLevel(text, ["TARGETS?", "TAKE\\s*PROFIT"]);
  return single ? [single] : [];
}

function normalizeCall(message, index) {
  if (!message || typeof message !== "object") return null;

  const raw = [
    message.signal,
    message.text,
    message.user_message,
    message.message,
    message.response,
    message.bot_response,
  ].filter(Boolean).join(" ");

  const explicitSide = String(firstValue(message.side, message.direction, message.action, "")).toUpperCase();
  const sideMatch = raw.match(/\b(LONG|SHORT|BUY|SELL)\b/i);
  const sideToken = explicitSide || sideMatch?.[1]?.toUpperCase();
  const side = sideToken === "BUY" ? "LONG" : sideToken === "SELL" ? "SHORT" : sideToken;
  if (!["LONG", "SHORT"].includes(side)) return null;

  let symbol = String(firstValue(message.symbol, message.pair, message.instrument, "")).toUpperCase();
  if (!symbol) {
    const pair = raw.toUpperCase().match(/\b([A-Z0-9]{2,12})[\/-]?(USDT|USDC|USD)\b/);
    if (pair) symbol = `${pair[1]}${pair[2]}`;
  }
  if (!symbol) return null;
  symbol = symbol.replace(/[^A-Z0-9]/g, "");

  const targets = Array.isArray(message.targets)
    ? message.targets
    : Array.isArray(message.take_profit)
      ? message.take_profit
      : parseTargets(raw);

  return {
    id: firstValue(message.id, message.message_id, `${message.timestamp || "signal"}-${index}`),
    symbol,
    side,
    entry: firstValue(message.entry, message.entry_price, parseTaggedLevel(raw, ["ENTRY", "ENTER"])),
    stop: firstValue(message.stop, message.stop_loss, message.sl, parseTaggedLevel(raw, ["STOP\\s*LOSS", "STOP", "SL"])),
    targets,
    confidence: cleanNumber(firstValue(message.confidence, message.score)),
    source: firstValue(message.channel, message.source, message.username && `@${message.username}`, "Telegram"),
    status: String(firstValue(message.execution_status, message.status, "RECEIVED")).toUpperCase(),
    timestamp: firstValue(message.timestamp, message.created_at, message.ts),
    raw: raw || "Structured signal received",
  };
}

function relativeTime(timestamp) {
  if (!timestamp) return "Just now";
  const time = new Date(timestamp).getTime();
  if (!Number.isFinite(time)) return "Received";
  const seconds = Math.max(0, Math.floor((Date.now() - time) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function buildCommand(draft, setup) {
  const fields = [
    `/aeon_trade ${draft.symbol.trim().toUpperCase() || "SYMBOL"} ${draft.side.toLowerCase()}`,
    `type=${draft.orderType.toLowerCase()}`,
    draft.entry ? `entry=${draft.entry.trim().replace(/\s+/g, "")}` : null,
    `risk=${draft.risk || setup.maxRisk}%`,
    draft.stop ? `sl=${draft.stop.trim().replace(/\s+/g, "")}` : "sl=required",
    draft.targets ? `tp=${draft.targets.trim().replace(/\s+/g, "")}` : null,
    `lev=${draft.leverage || setup.maxLeverage}x`,
    `route=${setup.executor === "unassigned" ? "hold" : setup.executor}`,
    `mode=${setup.mode}`,
  ];
  return fields.filter(Boolean).join(" ");
}

function StatusPill({ tone = "muted", children }) {
  return <span className={`api-trading-status is-${tone}`}><i />{children}</span>;
}

function PipelineNode({ icon: Icon, index, title, status, detail, tone, last }) {
  return (
    <div className={`api-flow-node is-${tone}`}>
      <div className="api-flow-icon"><Icon /></div>
      <div className="api-flow-copy">
        <span>0{index} / {status}</span>
        <strong>{title}</strong>
        <small>{detail}</small>
      </div>
      {!last ? <ChevronRight className="api-flow-arrow" /> : null}
    </div>
  );
}

function CallCard({ call }) {
  const isLong = call.side === "LONG";
  return (
    <article className={`api-call-card ${isLong ? "is-long" : "is-short"} ${call.isExample ? "is-example" : ""}`}>
      <div className="api-call-rail">
        <span>{call.side}</span>
        <i />
      </div>
      <div className="api-call-body">
        <header>
          <div>
            <span>{call.source}</span>
            <strong>{call.symbol}</strong>
          </div>
          <div className="api-call-meta">
            <span className={call.isExample ? "is-preview" : ""}>{call.status}</span>
            <small>{call.isExample ? "Example only" : relativeTime(call.timestamp)}</small>
          </div>
        </header>
        <div className="api-call-levels">
          <div><span>Entry</span><strong>{displayLevel(call.entry)}</strong></div>
          <div><span>Stop</span><strong>{displayLevel(call.stop)}</strong></div>
          <div>
            <span>Targets</span>
            <strong>{call.targets?.length ? call.targets.map(displayLevel).join(" · ") : "—"}</strong>
          </div>
          <div>
            <span>Confidence</span>
            <strong>{call.confidence !== null ? `${call.confidence}%` : "—"}</strong>
          </div>
        </div>
        <p>{call.raw}</p>
      </div>
    </article>
  );
}

export default function ApiTradingPage({ messages = [] }) {
  const [setup, setSetup] = useState(() => ({ ...DEFAULT_SETUP, ...readJson(STORAGE_KEY, {}) }));
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [remoteMessages, setRemoteMessages] = useState([]);
  const [telegramOnline, setTelegramOnline] = useState(false);
  const [weexIntelOnline, setWeexIntelOnline] = useState(false);
  const [weexSetupOpen, setWeexSetupOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [notice, setNotice] = useState("");
  const [drafts, setDrafts] = useState(() => {
    const saved = readJson(QUEUE_KEY, []);
    return Array.isArray(saved) ? saved : [];
  });
  const noticeTimer = useRef(null);

  const showNotice = useCallback((text) => {
    setNotice(text);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(""), 2600);
  }, []);

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setRefreshing(true);
    try {
      const [telegramResult, weexResult] = await Promise.allSettled([
        fetch(`${API}/bot/messages?limit=60`).then(async (response) => {
          if (!response.ok) throw new Error(`Telegram ${response.status}`);
          return response.json();
        }),
        fetch(`${API}/researcher/lab/weex`).then(async (response) => {
          if (!response.ok) throw new Error(`WEEX ${response.status}`);
          return response.json();
        }),
      ]);

      if (telegramResult.status === "fulfilled" && Array.isArray(telegramResult.value)) {
        setRemoteMessages(telegramResult.value);
        setTelegramOnline(true);
      } else {
        setTelegramOnline(false);
      }

      if (weexResult.status === "fulfilled") {
        const sources = weexResult.value?.sources || {};
        setWeexIntelOnline(Object.keys(sources).some((key) => key.startsWith("weex_")));
      } else {
        setWeexIntelOnline(false);
      }
    } finally {
      if (!quiet) setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refresh({ quiet: true });
    const timer = setInterval(() => refresh({ quiet: true }), 30000);
    return () => clearInterval(timer);
  }, [refresh]);

  const calls = useMemo(() => {
    const source = [...remoteMessages, ...messages];
    const seen = new Set();
    return source
      .map(normalizeCall)
      .filter(Boolean)
      .filter((call) => {
        const key = `${call.id}-${call.symbol}-${call.side}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 12);
  }, [messages, remoteMessages]);

  const command = useMemo(() => buildCommand(draft, setup), [draft, setup]);
  const executorAssigned = setup.executor !== "unassigned";
  const telegramRouted = Boolean(setup.telegramSource.trim());

  function updateSetup(key, value) {
    setSetup((current) => ({ ...current, [key]: value }));
  }

  function saveSetup() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(setup));
    showNotice("Setup preferences saved on this device");
  }

  async function copyCommand() {
    try {
      await navigator.clipboard.writeText(command);
      showNotice("Draft command copied");
    } catch {
      showNotice("Copy blocked by this browser");
    }
  }

  function queueDraft() {
    const item = {
      id: Date.now(),
      command,
      symbol: draft.symbol.toUpperCase(),
      side: draft.side,
      route: setup.executor,
      createdAt: new Date().toISOString(),
    };
    const next = [item, ...drafts].slice(0, 5);
    setDrafts(next);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(next));
    showNotice("Draft held locally — no order was sent");
  }

  return (
    <section className="api-trading-page">
      {notice ? <div className="api-trading-toast"><Check />{notice}</div> : null}

      <header className="api-trading-hero">
        <div className="api-trading-kicker">
          <span>AEON / Execution infrastructure</span>
          <StatusPill tone="amber">Setup mode</StatusPill>
        </div>
        <div className="api-trading-hero-row">
          <div>
            <h1>API Trading Desk</h1>
            <p>Telegram calls become structured AEON orders, pass a risk gate, then route to WEEX when an executor is assigned.</p>
          </div>
          <div className="api-trading-hero-actions">
            <button type="button" onClick={() => refresh()} disabled={refreshing}>
              <RefreshCw className={refreshing ? "animate-spin" : ""} />
              Refresh feeds
            </button>
            <button type="button" className="is-primary" onClick={() => document.getElementById("api-command-builder")?.scrollIntoView({ behavior: "smooth" })}>
              <Terminal />
              Build command
            </button>
          </div>
        </div>
        <div className="api-trading-lockline">
          <LockKeyhole />
          <span>Execution is locked. WEEX is read-only and the trade recipient is unassigned.</span>
        </div>
      </header>

      <div className="api-flow-grid" aria-label="Telegram to WEEX trade flow">
        <PipelineNode
          icon={Send}
          index={1}
          title="Telegram source"
          status={telegramRouted ? "ROUTED" : telegramOnline ? "BOT ONLINE" : "WAITING"}
          detail={telegramRouted ? setup.telegramSource : "Assign the calls channel"}
          tone={telegramRouted ? "ready" : telegramOnline ? "partial" : "muted"}
        />
        <PipelineNode
          icon={Bot}
          index={2}
          title="AEON call parser"
          status="READY"
          detail="LONG / SHORT schema"
          tone="ready"
        />
        <PipelineNode
          icon={ShieldCheck}
          index={3}
          title="Risk + approval"
          status={executorAssigned ? "MANUAL" : "HOLD"}
          detail={executorAssigned ? "Approval remains required" : "No executor assigned"}
          tone={executorAssigned ? "partial" : "muted"}
        />
        <PipelineNode
          icon={WalletCards}
          index={4}
          title="WEEX API"
          status={weexIntelOnline ? "READ-ONLY" : "OFFLINE"}
          detail={weexIntelOnline ? "Intel connected · orders locked" : "Waiting for server feed"}
          tone={weexIntelOnline ? "partial" : "muted"}
          last
        />
      </div>

      <div className="api-trading-layout">
        <section className="api-desk-panel api-feed-panel">
          <header className="api-panel-header">
            <div>
              <span><Radio /> Live call intake</span>
              <h2>Telegram → AEON Calls</h2>
              <p>Only messages containing a symbol and LONG / SHORT intent enter this feed.</p>
            </div>
            <div className="api-feed-health">
              <StatusPill tone={telegramOnline ? "green" : "muted"}>{telegramOnline ? "Feed online" : "No feed"}</StatusPill>
              <small>30s poll</small>
            </div>
          </header>

          <div className="api-call-list">
            {calls.length ? calls.map((call) => <CallCard key={call.id} call={call} />) : (
              <>
                <div className="api-feed-empty">
                  <Send />
                  <div>
                    <strong>No trade calls received yet</strong>
                    <span>Add the Telegram source below. AEON will ignore normal chat and keep only structured calls.</span>
                  </div>
                </div>
                <CallCard call={EXAMPLE_CALL} />
              </>
            )}
          </div>
        </section>

        <aside className="api-desk-panel api-setup-panel">
          <header className="api-panel-header">
            <div>
              <span><Settings2 /> Connection setup</span>
              <h2>Route the desk</h2>
              <p>Configure the source now; assign a trader or bot later.</p>
            </div>
          </header>

          <div className="api-setup-block">
            <div className="api-setup-title">
              <span className="api-setup-icon"><Send /></span>
              <div><strong>Telegram calls</strong><small>Inbound signal source</small></div>
              <StatusPill tone={telegramRouted ? "green" : "muted"}>{telegramRouted ? "Assigned" : "Needed"}</StatusPill>
            </div>
            <label className="api-field">
              <span>Channel, group, or username</span>
              <input
                value={setup.telegramSource}
                onChange={(event) => updateSetup("telegramSource", event.target.value)}
                placeholder="@aeon_calls or -100…"
              />
            </label>
            <small className="api-field-note"><Link2 /> Existing bot feed: @ObsidianCabalbot</small>
          </div>

          <div className="api-setup-block">
            <div className="api-setup-title">
              <span className="api-setup-icon"><KeyRound /></span>
              <div><strong>WEEX API trading</strong><small>Server-side credentials only</small></div>
              <StatusPill tone={weexIntelOnline ? "amber" : "muted"}>{weexIntelOnline ? "Read-only" : "Not connected"}</StatusPill>
            </div>
            <p className="api-setup-copy">Market and account intelligence is connected. Order permissions stay off until the server receives a dedicated trade-only key.</p>
            <button type="button" className="api-text-button" onClick={() => setWeexSetupOpen((open) => !open)}>
              <KeyRound /> {weexSetupOpen ? "Hide required access" : "View required access"} <ChevronRight />
            </button>
            {weexSetupOpen ? (
              <div className="api-key-requirements">
                <span><CheckCircle2 /> WEEX API key + secret + passphrase</span>
                <span><CheckCircle2 /> Futures order permission</span>
                <span><ShieldCheck /> Withdrawal permission disabled</span>
                <small>Credentials belong in the server vault, never browser storage or Telegram.</small>
              </div>
            ) : null}
          </div>

          <div className="api-setup-block">
            <div className="api-setup-title">
              <span className="api-setup-icon"><UserRound /></span>
              <div><strong>Trade executor</strong><small>Who receives approved calls</small></div>
              <StatusPill tone={executorAssigned ? "amber" : "muted"}>{executorAssigned ? "Manual" : "Unassigned"}</StatusPill>
            </div>
            <label className="api-field">
              <span>Routing mode</span>
              <select value={setup.executor} onChange={(event) => updateSetup("executor", event.target.value)}>
                <option value="unassigned">Unassigned — hold drafts</option>
                <option value="manual">Manual approval queue</option>
              </select>
            </label>
            <label className="api-field">
              <span>Trader / bot label (optional)</span>
              <input
                value={setup.executorName}
                onChange={(event) => updateSetup("executorName", event.target.value)}
                placeholder="Assign later"
              />
            </label>
          </div>

          <button type="button" className="api-save-setup" onClick={saveSetup}>
            <Check /> Save setup preferences
          </button>
        </aside>
      </div>

      <div className="api-trading-lower">
        <section id="api-command-builder" className="api-desk-panel api-command-panel">
          <header className="api-panel-header">
            <div>
              <span><Terminal /> Command builder</span>
              <h2>Prepare a trade instruction</h2>
              <p>Build a Telegram-ready command now. It stays a draft until the execution adapter and recipient are approved.</p>
            </div>
            <StatusPill tone="amber">Draft only</StatusPill>
          </header>

          <div className="api-command-form">
            <label className="api-field">
              <span>Market</span>
              <input value={draft.symbol} onChange={(event) => setDraft((current) => ({ ...current, symbol: event.target.value }))} />
            </label>
            <div className="api-field">
              <span>Direction</span>
              <div className="api-side-toggle">
                {["LONG", "SHORT"].map((side) => (
                  <button
                    key={side}
                    type="button"
                    className={`${draft.side === side ? "is-active" : ""} is-${side.toLowerCase()}`}
                    onClick={() => setDraft((current) => ({ ...current, side }))}
                  >
                    {side}
                  </button>
                ))}
              </div>
            </div>
            <label className="api-field">
              <span>Order type</span>
              <select value={draft.orderType} onChange={(event) => setDraft((current) => ({ ...current, orderType: event.target.value }))}>
                <option value="LIMIT">Limit</option>
                <option value="MARKET">Market</option>
              </select>
            </label>
            <label className="api-field">
              <span>Entry {draft.orderType === "MARKET" ? "(optional)" : ""}</span>
              <input value={draft.entry} onChange={(event) => setDraft((current) => ({ ...current, entry: event.target.value }))} placeholder="Price or range" />
            </label>
            <label className="api-field">
              <span>Stop loss</span>
              <input value={draft.stop} onChange={(event) => setDraft((current) => ({ ...current, stop: event.target.value }))} placeholder="Required before routing" />
            </label>
            <label className="api-field">
              <span>Targets</span>
              <input value={draft.targets} onChange={(event) => setDraft((current) => ({ ...current, targets: event.target.value }))} placeholder="TP1,TP2,TP3" />
            </label>
            <label className="api-field">
              <span>Risk / trade</span>
              <div className="api-input-suffix"><input value={draft.risk} onChange={(event) => setDraft((current) => ({ ...current, risk: event.target.value }))} /><i>%</i></div>
            </label>
            <label className="api-field">
              <span>Leverage cap</span>
              <div className="api-input-suffix"><input value={draft.leverage} onChange={(event) => setDraft((current) => ({ ...current, leverage: event.target.value }))} /><i>x</i></div>
            </label>
          </div>

          <div className="api-command-output">
            <div><Circle /><span>{command}</span></div>
            <button type="button" onClick={copyCommand} title="Copy command"><Copy /></button>
          </div>

          <div className="api-command-actions">
            <button type="button" onClick={copyCommand}><Copy /> Copy for Telegram</button>
            <button type="button" className="is-primary" onClick={queueDraft}><Route /> Hold in draft queue</button>
            <small><LockKeyhole /> No exchange order is sent.</small>
          </div>

          {drafts.length ? (
            <div className="api-draft-queue">
              <header><span>Recent local drafts</span><small>{drafts.length} held</small></header>
              {drafts.slice(0, 3).map((item) => (
                <div key={item.id}>
                  <span className={item.side === "LONG" ? "is-long" : "is-short"}>{item.side}</span>
                  <strong>{item.symbol}</strong>
                  <small>{relativeTime(item.createdAt)} · route {item.route}</small>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        <aside className="api-desk-panel api-policy-panel">
          <header className="api-panel-header">
            <div>
              <span><ShieldCheck /> Execution policy</span>
              <h2>Guardrails</h2>
              <p>Defaults for every call that reaches the approval queue.</p>
            </div>
          </header>

          <div className="api-policy-state">
            <div><ShieldCheck /><span>Current posture</span></div>
            <strong>HOLD FOR APPROVAL</strong>
            <p>Parser and drafts are active. Automated execution is locked.</p>
          </div>

          <div className="api-policy-fields">
            <label className="api-field">
              <span>Operating mode</span>
              <select value={setup.mode} onChange={(event) => updateSetup("mode", event.target.value)}>
                <option value="draft">Draft only</option>
                <option value="paper">Paper execution</option>
              </select>
            </label>
            <label className="api-field">
              <span>Max risk / trade</span>
              <div className="api-input-suffix"><input value={setup.maxRisk} onChange={(event) => updateSetup("maxRisk", event.target.value)} /><i>%</i></div>
            </label>
            <label className="api-field">
              <span>Max leverage</span>
              <div className="api-input-suffix"><input value={setup.maxLeverage} onChange={(event) => updateSetup("maxLeverage", event.target.value)} /><i>x</i></div>
            </label>
            <label className="api-field">
              <span>Daily stop</span>
              <div className="api-input-suffix"><input value={setup.dailyStop} onChange={(event) => updateSetup("dailyStop", event.target.value)} /><i>%</i></div>
            </label>
            <label className="api-field">
              <span>Approval</span>
              <select value={setup.approval} onChange={(event) => updateSetup("approval", event.target.value)}>
                <option value="always">Every trade</option>
                <option value="two-step">Two-step approval</option>
              </select>
            </label>
          </div>

          <div className="api-locked-control">
            <span><Zap /> Auto-execute calls</span>
            <button type="button" disabled><i /></button>
            <small>Locked until WEEX trading, a recipient, and a live risk review are complete.</small>
          </div>

          <div className="api-safety-note">
            <AlertTriangle />
            <p><strong>Trade-only API key.</strong> Keep withdrawals disabled and start with paper or minimum-size orders when the adapter is added.</p>
          </div>
        </aside>
      </div>

      <footer className="api-trading-footer">
        <span><Activity /> TELEGRAM INTAKE {telegramOnline ? "ONLINE" : "WAITING"}</span>
        <span><Bot /> PARSER READY</span>
        <span><ShieldCheck /> APPROVAL REQUIRED</span>
        <span><WalletCards /> WEEX {weexIntelOnline ? "READ-ONLY" : "OFFLINE"}</span>
      </footer>
    </section>
  );
}
